/**
 * Cascade admin portal — typed config (MPF-style split: this package is
 * config only; the UI lives in apps/admin-cascade-web and the API in
 * apps/admin-cascade-api).
 *
 * Data ownership (Cascade V1):
 *   - Airtable is the source of truth for human-readable business records:
 *     Leads, Contacts, Organizations, Form Submissions, lead status, owner,
 *     next follow-up, source/campaign fields.
 *   - Supabase (dedicated Cascade project) owns operational/system data:
 *     activity timeline, AI receptionist call logs/transcripts, webhook
 *     payload archives, audit logs, AI usage logs.
 * The UI presents both as one unified LeadLoop experience; no field is owned
 * by both sides.
 */

import { createConnectorConfig } from '@command-center/connector-sdk';
import {
  BRANDING_ASSET_PATHS,
  createPortalApp,
  type PortalAppConfig,
} from '@command-center/core-platform';
import { getPortal } from '@command-center/portal-registry';
import { createTableDefinition } from '@command-center/data-sdk';
import { createPortalModule } from '@command-center/module-sdk';

/*
  Navigation is five destinations and no section headings.

  The portal previously carried eleven items across four labelled groups
  (Overview / Feeds / Records / System) plus a Dashboard that restated LeadLoop.
  A client signing in has five questions — what needs attention, where are the
  Leads, the Form Entries, the Calls, and the People — and every extra row was
  a sixth thing to read before answering one of them.

  Everything removed from the nav still exists and is still routed:
  Organizations moved inside People, and Emails, Activity, Switchboard, and
  Alice moved inside the single Settings destination reached from the account
  menu. Nothing was deleted; secondary functions simply stopped competing with
  the queue.
*/
const leadLoopModule = createPortalModule({
  id: 'cascade.leadloop',
  label: 'LeadLoop',
  route: '/leadloop',
  icon: 'Repeat',
  appTypes: ['admin'],
  requiredConnectors: ['cascade-airtable'],
});

const leadsModule = createPortalModule({
  id: 'cascade.leads',
  label: 'Leads',
  route: '/leads',
  icon: 'UserPlus',
  appTypes: ['admin'],
  requiredConnectors: ['cascade-airtable'],
});

const submissionsModule = createPortalModule({
  id: 'cascade.submissions',
  label: 'Form Entries',
  route: '/forms-feed',
  icon: 'ClipboardList',
  appTypes: ['admin'],
  requiredConnectors: ['cascade-airtable'],
});

const voiceAgentsModule = createPortalModule({
  id: 'cascade.voice-agents',
  label: 'Calls',
  route: '/ai-systems/voice-agents',
  icon: 'PhoneCall',
  appTypes: ['admin'],
  requiredConnectors: ['cascade-airtable'],
});

/** Contacts and Organizations, one destination, tabbed. */
const peopleModule = createPortalModule({
  id: 'cascade.people',
  label: 'People',
  route: '/contacts',
  icon: 'Users',
  appTypes: ['admin'],
  requiredConnectors: ['cascade-airtable'],
});

/* Routed but deliberately absent from the main navigation: reached from the
   account menu (Settings) or from inside Settings itself. */
const switchboardModule = createPortalModule({
  id: 'cascade.switchboard',
  label: 'Switchboard',
  route: '/switchboard',
  icon: 'ToggleRight',
  appTypes: ['admin'],
});

const settingsModule = createPortalModule({
  id: 'cascade.settings',
  label: 'Settings',
  route: '/settings',
  icon: 'Settings',
  appTypes: ['admin'],
});

const portal = getPortal('cascade');

export const config: PortalAppConfig = {
  appId: 'admin-cascade',
  appType: 'admin',
  clientId: 'cascade',
  clientName: 'Cascade',
  branding: {
    name: portal.branding.name,
    logo: BRANDING_ASSET_PATHS.logo,
    iconSrc: BRANDING_ASSET_PATHS.icon,
    icon: portal.branding.iconText,
    /*
      Cascade's brand colour is blue; One Up's maroon is the ui-system DEFAULT
      (tokens.css --primary: #79141a) and is what a portal inherits when it
      passes nothing here.

      `accent` is NOT a second brand colour. In ui-system it is the quiet hover
      surface — sidebar rows, outline/ghost buttons, focused select options —
      and it is always paired with `accent-foreground`, which is near-black.
      Putting a saturated brand blue in that slot turned every hover into solid
      blue behind dark text. It stays a pale blue-tinted neutral so `primary` is
      the only thing on screen carrying the brand.
    */
    colors: portal.branding.colors,
  },
  alice: {
    enabled: true,
    defaultState: 'open',
    assistantName: 'Alice',
    assistantSubtitle: 'LeadLoop help for the record or queue on screen.',
    defaultModelLabel: 'Fast',
    portalScope: 'client',
    allowedContextSources: ['page', 'lead', 'contact', 'organization', 'form_entry', 'call'],
    allowedActions: ['read_context', 'draft_text', 'suggest_field_values'],
    blockedActions: [
      'send_email_without_approval',
      'delete_records',
      'publish_changes',
      'charge_client',
      'change_permissions',
      'access_unscoped_client_data',
    ],
    enabledModes: ['global', 'context', 'composer'],
    enabledComposerTargets: ['email_reply', 'call_summary', 'generic'],
    quickActionsEnabled: false,
    contextBarEnabled: true,
    suggestedActionsEnabled: false,
    voiceEnabledPlaceholder: false,
    fileUploadEnabledPlaceholder: false,
    cameraEnabledPlaceholder: false,
  },
  /*
    No `group` on any item: the web app renders these as one unlabelled list.
    The rendered order is this array's order.
  */
  navigation: [
    {
      id: 'leadloop',
      label: 'LeadLoop',
      route: '/leadloop',
      icon: 'Repeat',
      moduleId: 'cascade.leadloop',
      description: 'Everything waiting on a decision, and how the week is going',
    },
    {
      id: 'leads',
      label: 'Leads',
      route: '/leads',
      icon: 'UserPlus',
      moduleId: 'cascade.leads',
      description: 'Every opportunity, and what happens next',
    },
    {
      id: 'submissions',
      label: 'Form Entries',
      route: '/forms-feed',
      icon: 'ClipboardList',
      moduleId: 'cascade.submissions',
      sourceName: 'Supabase forms + form_submissions',
      description: 'Website form entries and the forms that produced them',
    },
    {
      id: 'voice-agents',
      label: 'Calls',
      route: '/ai-systems/voice-agents',
      icon: 'PhoneCall',
      moduleId: 'cascade.voice-agents',
      sourceName: 'Supabase vapi_calls + voice agents',
      description: 'Call history, and the voice agents that answered',
    },
    {
      id: 'people',
      label: 'People',
      route: '/contacts',
      icon: 'Users',
      moduleId: 'cascade.people',
      description: 'Contacts and the organizations behind them',
    },
  ],
  modules: [
    leadLoopModule,
    leadsModule,
    submissionsModule,
    voiceAgentsModule,
    peopleModule,
    switchboardModule,
    settingsModule,
  ],
  connectors: [
    createConnectorConfig({
      id: 'cascade-airtable',
      type: 'airtable',
      env: {
        apiKey: 'AIRTABLE_API_KEY',
        defaultBaseId: 'AIRTABLE_BASE_ID',
      },
    }),
  ],
  // Airtable business tables. Field mappings firm up in the data-homes pass
  // (docs/cascade/airtable-schema.md) once the dedicated Cascade base exists;
  // table names are read by the API from wrangler vars so the base can be
  // swapped without code changes.
  tables: [
    createTableDefinition({
      id: 'cascade.leads',
      label: 'Leads',
      source: 'airtable',
      connectorId: 'cascade-airtable',
      tableName: 'Leads',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'title', label: 'Lead', type: 'string', sourceField: 'Lead', required: true },
        { id: 'status', label: 'Status', type: 'select', sourceField: 'Status' },
        { id: 'owner', label: 'Owner', type: 'string', sourceField: 'Owner' },
        { id: 'nextFollowUpAt', label: 'Next Follow-Up', type: 'date', sourceField: 'Next Follow-Up' },
        { id: 'nextStep', label: 'Next Step', type: 'string', sourceField: 'Next Step' },
        { id: 'source', label: 'Source', type: 'select', sourceField: 'Source' },
        { id: 'campaign', label: 'Campaign', type: 'string', sourceField: 'Campaign' },
        { id: 'notes', label: 'Notes', type: 'string', sourceField: 'Notes' },
      ],
    }),
    createTableDefinition({
      id: 'cascade.contacts',
      label: 'Contacts',
      source: 'airtable',
      connectorId: 'cascade-airtable',
      tableName: 'Contacts',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'fullName', label: 'Name', type: 'string', sourceField: 'Name', required: true },
        { id: 'email', label: 'Email', type: 'string', sourceField: 'Email' },
        { id: 'phone', label: 'Phone', type: 'string', sourceField: 'Phone' },
        { id: 'organizationIds', label: 'Organization', type: 'string', sourceField: 'Organization' },
        { id: 'aiContactOverview', label: 'AI Contact Overview', type: 'string', sourceField: 'AI Contact Overview', readonly: true },
      ],
    }),
    createTableDefinition({
      id: 'cascade.organizations',
      label: 'Organizations',
      source: 'airtable',
      connectorId: 'cascade-airtable',
      tableName: 'Organizations',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'name', label: 'Name', type: 'string', sourceField: 'Name', required: true },
        { id: 'website', label: 'Website', type: 'string', sourceField: 'Website' },
        { id: 'phone', label: 'Phone', type: 'string', sourceField: 'Phone' },
        { id: 'status', label: 'Status', type: 'select', sourceField: 'Status' },
      ],
    }),
    createTableDefinition({
      id: 'cascade.submissions',
      label: 'Form Submissions',
      source: 'airtable',
      connectorId: 'cascade-airtable',
      tableName: 'Form Submissions',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'formName', label: 'Form', type: 'string', sourceField: 'Form', required: true },
        { id: 'submittedName', label: 'Name', type: 'string', sourceField: 'Name' },
        { id: 'submittedEmail', label: 'Email', type: 'string', sourceField: 'Email' },
        { id: 'submittedPhone', label: 'Phone', type: 'string', sourceField: 'Phone' },
        { id: 'leadIds', label: 'Lead', type: 'string', sourceField: 'Lead' },
      ],
    }),
    createTableDefinition({
      id: 'cascade.ai-agents',
      label: 'AI Agents',
      source: 'airtable',
      connectorId: 'cascade-airtable',
      tableName: 'Ai_Agents',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'name', label: 'Agent Name', type: 'string', sourceField: 'Agent Name', readonly: true },
        { id: 'status', label: 'Profile Status', type: 'select', sourceField: 'Agent Profile Status', readonly: true },
        { id: 'type', label: 'Agent Type', type: 'string', sourceField: 'Agent Type', readonly: true },
        { id: 'client', label: 'Assigned Client', type: 'string', sourceField: 'Assigned Client', readonly: true },
        { id: 'connection', label: 'Connection', type: 'select', sourceField: 'Connection Status', readonly: true },
        { id: 'voice', label: 'Voice', type: 'string', sourceField: 'Voice Type', readonly: true },
        { id: 'vapiAssistantId', label: 'Vapi Assistant ID', type: 'string', sourceField: 'vapi_agent_id', readonly: true },
      ],
    }),
    createTableDefinition({
      id: 'cascade.ai-calls',
      label: 'Calls',
      source: 'airtable',
      connectorId: 'cascade-airtable',
      tableName: 'Ai_Calls',
      baseIdEnv: 'AIRTABLE_BASE_ID',
      fields: [
        { id: 'callId', label: 'Call ID', type: 'string', sourceField: 'call_id', readonly: true },
        { id: 'caller', label: 'Caller', type: 'string', sourceField: 'caller_full_name', readonly: true },
        { id: 'phone', label: 'Phone', type: 'string', sourceField: 'phone_number', readonly: true },
        { id: 'status', label: 'Status', type: 'select', sourceField: 'Status', readonly: true },
        { id: 'type', label: 'Type', type: 'string', sourceField: 'Type Of Call', readonly: true },
        { id: 'startedAt', label: 'Started', type: 'datetime', sourceField: 'started_at', readonly: true },
        { id: 'duration', label: 'Duration', type: 'number', sourceField: 'duration_minutes', readonly: true },
        { id: 'summary', label: 'Summary', type: 'string', sourceField: 'call_summary', readonly: true },
      ],
    }),
  ],
  featureFlags: {
    alice_enabled: false,
    mailgun_approval_enabled: false,
    ai_call_ingest_enabled: false,
    public_portal_enabled: false,
    write_actions_enabled: false,
  },
};

export default createPortalApp(config);
